import CorrectionEvidence from '../components/sales/CorrectionEvidence';
import EvidenceFiles from '../components/sales/EvidenceFiles';
import ClientCorrectionHistory from '../components/sales/ClientCorrectionHistory';
import PurchaserFields from '../components/sales/PurchaserFields';
import { purchaserName, purchaserInitials } from '../lib/purchaser';
import { useConfirmation } from '../context/ConfirmationContext';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import {
  GlassCard, Button, Badge, InputField, Modal,
  SectionHeader, DataTable, Divider, StatCard,
} from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import './Clients.css';

// ─── Types ──────────────────────────────────────────────────

interface Beneficiary {
  designation?: string;
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  phone?: string;
  email?: string;
  birthday?: string;
}

interface ContactLog {
  id: string;
  type: string;
  subject: string;
  body?: string;
  createdAt: string;
  agent: { id: string; name: string };
}

interface SaleRef {
  id: string;
  saleAmount: number;
  totalPaid: number;
  balance: number;
  stage: string;
  lot: { lotNumber: string; section?: { label: string } };
  createdAt: string;
}

interface LedgerEntry {
  id: string;
  type: string;
  amount: number;
  description: string;
  createdAt: string;
}

interface Client {
  purchaserType?: string;
  companyName?: string;
  middleName?: string;
  purchaserDetails?: Record<string, string>;
  updatedAt: string;
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  address?: string;
  birthday?: string;
  occupation?: string;
  incomeBracket?: string;
  source: string;
  status: string;
  emergencyName?: string;
  emergencyPhone?: string;
  notes?: string;
  assignedAgent?: { id: string; name: string; email: string };
  beneficiaries: Beneficiary[];
  contactLogs: ContactLog[];
  sales: SaleRef[];
  ledgerEntries?: LedgerEntry[];
  _count?: { beneficiaries: number; sales: number; contactLogs: number; corrections?: number };
  createdAt: string;
}

type View = 'list' | 'detail' | 'form';

const CLIENT_STATUSES = ['LEAD', 'PROSPECT', 'ACTIVE', 'INACTIVE'] as const;
const CLIENT_SOURCES = ['WALK_IN', 'REFERRAL', 'ONLINE', 'SOCIAL_MEDIA', 'EVENT', 'OTHER'] as const;
const INCOME_BRACKETS = ['Below 20K', '20K-50K', '50K-100K', '100K+'] as const;
const RELATIONSHIPS = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'] as const;
const CONTACT_TYPES = ['CALL', 'EMAIL', 'VISIT', 'SMS', 'NOTE'] as const;
const PAGE_SIZE = 20;
interface ClientStats { total: number; leads: number; prospects: number; active: number; inactive: number }

// ─── Helpers ────────────────────────────────────────────────

const statusVariant = (s: string) => {
  switch (s) {
    case 'ACTIVE': return 'available';
    case 'PROSPECT': return 'info';
    case 'LEAD': return 'reserved';
    case 'INACTIVE': return 'sold';
    default: return 'default';
  }
};

const stageVariant = (s: string) => {
  switch (s) {
    case 'COMPLETED': return 'available';
    case 'PAYMENT': case 'CONTRACT_SIGNING': return 'info';
    case 'INQUIRY': case 'RESERVATION': return 'reserved';
    case 'CANCELLED': return 'sold';
    default: return 'default';
  }
};

const contactIcon = (type: string) => {
  switch (type) {
    case 'CALL': return 'solar:phone-bold-duotone';
    case 'EMAIL': return 'solar:letter-bold-duotone';
    case 'VISIT': return 'solar:map-point-bold-duotone';
    case 'SMS': return 'solar:chat-round-bold-duotone';
    case 'NOTE': return 'solar:document-text-bold-duotone';
    default: return 'solar:info-circle-bold-duotone';
  }
};

const ledgerTypeConfig: Record<string, { label: string; icon: string; color: string }> = {
  PAYMENT: { label: 'Payment', icon: 'solar:wallet-money-bold-duotone', color: '#10B981' },
  PENALTY: { label: 'Penalty', icon: 'solar:danger-triangle-bold-duotone', color: '#EF4444' },
  ADJUSTMENT: { label: 'Adjustment', icon: 'solar:pen-new-square-bold-duotone', color: '#F59E0B' },
  REFUND: { label: 'Refund', icon: 'solar:undo-left-bold-duotone', color: '#8B5CF6' },
  DOWN_PAYMENT: { label: 'Down Payment', icon: 'solar:hand-money-bold-duotone', color: '#0D9488' },
};

const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const fmt = (n: number) => new Intl.NumberFormat('en-PH', {
  style: 'currency', currency: 'PHP', minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(n);

// ─── Component ──────────────────────────────────────────────

const Clients: React.FC = () => {
  const { addToast } = useToast();
  const { confirmAction } = useConfirmation();
  const [view, setView] = useState<View>('list');
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [ledgerError, setLedgerError] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Client | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPurchaserType, setFilterPurchaserType] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const requestId = useRef(0);
  const searchPending = search.trim() !== debouncedSearch;

  // Form state
  const [formData, setFormData] = useState<any>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [beneficiaryForm, setBeneficiaryForm] = useState<any>({});
  const [showBeneficiaryModal, setShowBeneficiaryModal] = useState(false);
  const [removingBeneficiary, setRemovingBeneficiary] = useState<Beneficiary | null>(null);
  const [removalContext, setRemovalContext] = useState<{ correctionReason: string; evidenceReference: string; evidenceVersionId?: string | null }>({ correctionReason: '', evidenceReference: '' });
  const [beneficiarySaving, setBeneficiarySaving] = useState(false);
  const beneficiaryOperation = useRef(false);

  // Contact log form
  const [showLogModal, setShowLogModal] = useState(false);
  const [logForm, setLogForm] = useState<any>({ type: 'CALL', subject: '', body: '' });

  // Portal account state
  const [portalStatus, setPortalStatus] = useState<any>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  // ─── Fetch clients from API ─────────────────────────────
  const fetchClients = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      setLoading(true);
      setLoadError(false);
      const data = await api.clients.list({
        page: String(page), limit: String(PAGE_SIZE),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(filterPurchaserType ? { purchaserType: filterPurchaserType } : {}),
      });
      if (currentRequest !== requestId.current) return;
      if (data.totalPages > 0 && page > data.totalPages) {
        setPage(data.totalPages);
        return;
      }
      const list = (data.clients || []).map((c: any) => ({
        ...c,
        beneficiaries: c.beneficiaries || [],
        contactLogs: c.contactLogs || [],
        sales: c.sales || [],
      }));
      setClients(list);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch {
      if (currentRequest === requestId.current) setLoadError(true);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [page, debouncedSearch, filterStatus, filterPurchaserType]);

  const fetchStats = useCallback(async () => {
    try { setStats(await api.clients.stats()); }
    catch { setStats(null); }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => { setPage(1); setDebouncedSearch(search.trim()); }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [view]);

  // ─── Filtered list ──────────────────────────────────────
  // ─── Table columns ─────────────────────────────────────
  const columns = [
    {
      key: 'name', header: 'Client',
      render: (row: Client) => (
        <button type="button" className="clients-name-button" onClick={() => openDetail(row)} disabled={openingId === row.id}>
          <span className="clients-avatar" aria-hidden="true">{purchaserInitials(row)}</span>
          <span className="clients-name-copy">
            <strong>{purchaserName(row)}</strong>
            <span>{row.email || row.phone}</span>
          </span>
        </button>
      ),
    },
    { key: 'phone', header: 'Phone' },
    {
      key: 'source', header: 'Source',
      render: (row: Client) => <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{row.source.replace(/_/g, ' ')}</span>,
    },
    {
      key: 'status', header: 'Status',
      render: (row: Client) => <Badge variant={statusVariant(row.status) as any}>{row.status}</Badge>,
    },
    {
      key: 'agent', header: 'Agent',
      render: (row: Client) => <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{row.assignedAgent?.name || '—'}</span>,
    },
    {
      key: 'sales', header: 'Sales',
      render: (row: Client) => <span style={{ color: 'var(--text-secondary)' }}>{row._count?.sales || 0}</span>,
    },
  ];

  // ─── Portal Account ────────────────────────────────────
  const fetchPortalStatus = useCallback(async (clientId: string) => {
    try {
      const data = await api.clientAuth.status(clientId);
      setPortalStatus(data);
    } catch { setPortalStatus(null); }
  }, []);

  // ─── Handlers ───────────────────────────────────────────

  const openDetail = useCallback(async (client: Client) => {
    setOpeningId(client.id);
    setLedgerError(false);
    setShowAllLogs(false);
    try {
      const full = await api.clients.get(client.id);
      // Fetch ledger entries for this client
      let ledgerEntries: LedgerEntry[] = [];
      try {
        const ledgerData = await api.payments.ledger.list({ clientId: client.id });
        ledgerEntries = ledgerData.entries || [];
      } catch (e) {
        console.error('Failed to fetch ledger:', e);
        setLedgerError(true);
      }
      setSelected({ 
        ...full, 
        beneficiaries: full.beneficiaries || [], 
        contactLogs: full.contactLogs || [], 
        sales: full.sales || [],
        ledgerEntries 
      });
      setView('detail');
      fetchPortalStatus(client.id);
    } catch {
      addToast('error', 'Could not open client', 'Please try again.');
    } finally {
      setOpeningId(null);
    }
  }, [addToast, fetchPortalStatus]);

  const openCreateForm = () => {
    setEditingId(null);
    setFormData({
      purchaserType: 'INDIVIDUAL', companyName: '', middleName: '', purchaserDetails: {},
      firstName: '', lastName: '', email: '', phone: '', address: '',
      birthday: '', occupation: '', incomeBracket: '', source: 'WALK_IN',
      status: 'LEAD', emergencyName: '', emergencyPhone: '', notes: '',
    });
    setView('form');
  };

  const openEditForm = (client: Client) => {
    setEditingId(client.id);
    setFormData({
      correctionReason: '', evidenceReference: '',
      purchaserType: client.purchaserType || 'UNVERIFIED', companyName: client.companyName || '', middleName: client.middleName || '', purchaserDetails: client.purchaserDetails || {}, expectedUpdatedAt: client.updatedAt,
      firstName: client.firstName, lastName: client.lastName,
      email: client.email || '', phone: client.phone, address: client.address || '',
      birthday: client.birthday?.split('T')[0] || '', occupation: client.occupation || '',
      incomeBracket: client.incomeBracket || '', source: client.source,
      status: client.status, emergencyName: client.emergencyName || '',
      emergencyPhone: client.emergencyPhone || '', notes: client.notes || '',
    });
    setView('form');
  };

  const handleSaveClient = async () => {
    if (saving) return;
    if (editingId && (!formData.correctionReason?.trim() || !formData.evidenceReference?.trim())) {
      addToast('error', 'Correction details required', 'Enter a correction reason and evidence reference before saving.');
      return;
    }
    if (!formData.phone?.trim() || (formData.purchaserType === 'CORPORATE' ? !formData.companyName?.trim() : !formData.firstName?.trim() || !formData.lastName?.trim())) {
      addToast('error', 'Validation Error', 'Phone and the purchaser’s name are required. Corporate purchasers need a company name.');
      return;
    }
    if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      addToast('error', 'Validation Error', 'Enter a valid email address.');
      return;
    }

    try {
      setSaving(true);
      if (editingId) {
        const updated = await api.clients.update(editingId, formData);
        setClients((prev) => prev.map((c) => c.id === editingId ? { ...c, ...updated } : c));
        setSelected((prev) => prev && prev.id === editingId ? { ...prev, ...updated } : prev);
        addToast('success', 'Client Updated', `${purchaserName(formData)} has been updated.`);
      } else {
        await api.clients.create(formData);
        addToast('success', 'Client Created', `${purchaserName(formData)} has been added.`);
      }
      setView(editingId && selected ? 'detail' : 'list');
      if (editingId || page === 1) fetchClients();
      else setPage(1);
      fetchStats();
    } catch (err: any) {
      addToast('error', 'Save Error', err.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async (clientId: string) => {
    setInviteLoading(true);
    try {
      const data = await api.clientAuth.invite(clientId);
      addToast('success', 'Invitation accepted by Postal', `Postal accepted the email to ${data.email}. Inbox delivery may take a moment.`);
      fetchPortalStatus(clientId);
    } catch (err: any) {
      addToast('error', 'Invitation not confirmed', err.message || 'Could not send invitation');
      fetchPortalStatus(clientId);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeleteClient = async (id: string) => {
    if (!selected || selected.sales.length > 0 || selected._count?.corrections) { addToast('error', 'Client history is retained', 'Clients with sales or profile correction history cannot be deleted.'); return; }
    await confirmAction({ title: `Delete ${purchaserName(selected)}?`, description: 'This permanently removes the client record, beneficiaries, and contact history. This cannot be undone.', confirmLabel: 'Delete client', successTitle: 'Client deleted', errorTitle: 'Client not deleted', action: async () => {
      await api.clients.delete(id);
      setView('list'); setSelected(null);
      if (clients.length === 1 && page > 1) setPage(page - 1); else fetchClients();
      fetchStats();
    }});
  };

  const applyBeneficiaryChange = (beneficiaries: Beneficiary[], result: any) => {
    if (!selected) return;
    const updated = { ...selected, beneficiaries, updatedAt: result.clientUpdatedAt, _count: { beneficiaries: beneficiaries.length, sales: selected.sales.length, contactLogs: selected.contactLogs.length, corrections: result.correctionCount } };
    setSelected(updated);
    setClients(prev => prev.map(c => c.id === selected.id ? { ...c, ...updated } : c));
  };

  const handleAddBeneficiary = async () => {
    if (!selected || beneficiaryOperation.current) return;
    if (!beneficiaryForm.firstName?.trim() || !beneficiaryForm.lastName?.trim() || !beneficiaryForm.relationship) {
      addToast('error', 'Validation Error', 'First name, last name, and relationship are required.'); return;
    }
    if (!beneficiaryForm.correctionReason?.trim() || !beneficiaryForm.evidenceReference?.trim()) {
      addToast('error', 'Correction details required', 'Enter a reason and evidence reference for this beneficiary addition.'); return;
    }
    beneficiaryOperation.current = true; setBeneficiarySaving(true);
    try {
      const result = await api.clients.addBeneficiary(selected.id, { ...beneficiaryForm, expectedUpdatedAt: selected.updatedAt });
      applyBeneficiaryChange([...selected.beneficiaries, result], result);
      setBeneficiaryForm({}); setShowBeneficiaryModal(false);
      addToast('success', 'Beneficiary Added');
    } catch (err: any) { addToast('error', 'Beneficiary not added', err.message || 'Failed to add beneficiary'); }
    finally { beneficiaryOperation.current = false; setBeneficiarySaving(false); }
  };

  const handleRemoveBeneficiary = async () => {
    if (!selected || !removingBeneficiary || beneficiaryOperation.current) return;
    if (!removalContext.correctionReason.trim() || !removalContext.evidenceReference.trim()) {
      addToast('error', 'Correction details required', 'Enter a reason and evidence reference for this beneficiary removal.'); return;
    }
    beneficiaryOperation.current = true; setBeneficiarySaving(true);
    try {
      const result = await api.clients.deleteBeneficiary(removingBeneficiary.id, { ...removalContext, expectedUpdatedAt: selected.updatedAt });
      applyBeneficiaryChange(selected.beneficiaries.filter(b => b.id !== removingBeneficiary.id), result);
      setRemovingBeneficiary(null);
      addToast('success', 'Beneficiary removed', 'The previous details remain in correction history.');
    } catch (err: any) { addToast('error', 'Beneficiary not removed', err.message || 'Failed to remove beneficiary'); }
    finally { beneficiaryOperation.current = false; setBeneficiarySaving(false); }
  };

  const handleAddLog = async () => {
    if (!logForm.subject) {
      addToast('error', 'Validation Error', 'Subject is required.');
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem('vop_user') || '{}');
      const newLog = await api.clients.addContactLog(selected!.id, {
        agentId: user.id,
        type: logForm.type,
        subject: logForm.subject,
        body: logForm.body,
      });
      if (selected) {
        const updated = { ...selected, contactLogs: [newLog, ...selected.contactLogs] };
        setSelected(updated);
        setClients((prev) => prev.map((c) => c.id === selected.id ? updated : c));
      }
      setLogForm({ type: 'CALL', subject: '', body: '' });
      setShowLogModal(false);
      addToast('success', 'Contact Log Added');
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to add contact log');
    }
  };

  // ─── RENDER: List View ──────────────────────────────────

  const renderListView = () => (
    <>
      <TopBar title="Clients" showSearch={false} />
      <div className="clients-page">
        <div className="clients-heading">
          <div><h2>Client directory</h2><p>Find records, review sales, and keep contact history current.</p></div>
          <Button variant="primary" icon="solar:add-circle-bold-duotone" onClick={openCreateForm}>Add client</Button>
        </div>

        <div className="clients-summary" aria-label="Client counts by status">
          {([
            ['Total clients', stats?.total], ['Leads', stats?.leads], ['Prospects', stats?.prospects],
            ['Active', stats?.active], ['Inactive', stats?.inactive],
          ] as [string, number | undefined][]).map(([label, value]) => (
            <div className="clients-summary-item" key={label}><span>{label}</span><strong>{value ?? '—'}</strong></div>
          ))}
        </div>

        <div className="clients-toolbar">
          <div className="clients-search">
            <InputField label="Search clients" placeholder="Name, email, or phone" icon="solar:magnifer-bold-duotone"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="clients-filter">
            <label htmlFor="clients-status-filter">Status</label>
            <select id="clients-status-filter" value={filterStatus} onChange={(e) => { setPage(1); setFilterStatus(e.target.value); }}>
              <option value="">All statuses</option>
              {CLIENT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
            </select>
          </div>
          <div className="clients-filter">
            <label htmlFor="clients-purchaser-filter">Purchaser type</label>
            <select id="clients-purchaser-filter" value={filterPurchaserType} onChange={e => { setPage(1); setFilterPurchaserType(e.target.value); }}>
              <option value="">All purchaser types</option><option value="UNVERIFIED">Unverified — needs review</option><option value="INDIVIDUAL">Individual</option><option value="CORPORATE">Corporate</option>
            </select>
          </div>
          <div className="clients-result-count" role="status">{loadError ? 'Results unavailable' : loading || searchPending ? 'Loading results…' : `${total} ${total === 1 ? 'client' : 'clients'} found`}</div>
        </div>

        {loadError ? <div className="clients-state" role="alert"><strong>Could not load clients</strong><p>Check the connection, then try again.</p><Button variant="secondary" onClick={fetchClients}>Try again</Button></div>
          : loading || searchPending ? <div className="clients-state" role="status">Loading client records…</div>
          : clients.length > 0 ? <>
            <p className="clients-table-hint">Swipe the table to see more columns.</p>
            <div className="clients-table-scroll"><DataTable columns={columns} data={clients} /></div>
            <div className="clients-pagination">
              <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
              {totalPages > 1 && <div>
                <Button variant="secondary" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>Previous</Button>
                <span>Page {page} of {totalPages}</span>
                <Button variant="secondary" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Next</Button>
              </div>}
            </div>
          </> : <div className="clients-state">
            <strong>{search || filterStatus || filterPurchaserType ? 'No matching clients' : 'No clients yet'}</strong>
            <p>{search || filterStatus || filterPurchaserType ? 'Try a different name, phone, status, or purchaser type.' : 'Add your first client to start tracking contacts and sales.'}</p>
            {(search || filterStatus || filterPurchaserType) && <Button variant="secondary" onClick={() => { setSearch(''); setFilterStatus(''); setFilterPurchaserType(''); setPage(1); }}>Clear filters</Button>}
          </div>}
      </div>
    </>
  );

  // ─── RENDER: Detail View ────────────────────────────────

  const renderDetailView = () => {
    if (!selected) return null;

    // Calculate financial summary
    const includedSales = selected.sales.filter(s => s.stage !== 'CANCELLED');
    const totalSalesAmount = includedSales.reduce((sum, s) => sum + s.saleAmount, 0);
    const totalPaid = includedSales.reduce((sum, s) => sum + (s.totalPaid || 0), 0);
    const totalBalance = includedSales.reduce((sum, s) => sum + (s.balance || 0), 0);

    return (
      <>
        <TopBar title="Client details" showSearch={false} />
        <div className="clients-detail-page">
          {/* Back + Actions */}
          <div className="clients-detail-actions">
            <Button variant="ghost" icon="solar:arrow-left-bold" onClick={() => { setView('list'); setSelected(null); }}>
              Back to Clients
            </Button>
            <div className="clients-action-spacer" />
            <Button variant="secondary" size="sm" icon="solar:chat-round-bold-duotone" 
              onClick={() => { setLogForm({ type: 'CALL', subject: '', body: '' }); setShowLogModal(true); }}>
              Quick Log
            </Button>
            <Button variant="secondary" size="sm" icon="solar:pen-bold-duotone" onClick={() => openEditForm(selected)}>Edit</Button>
            <Button variant="secondary" size="sm" icon="solar:trash-bin-trash-bold-duotone" onClick={() => handleDeleteClient(selected.id)} disabled={selected.sales.length > 0 || !!selected._count?.corrections} style={{ color: 'var(--danger)' }}>Delete</Button>
          </div>

          <GlassCard hoverable={false} className="clients-profile-card">
            <div className="clients-profile-heading">
              <div className="clients-profile-identity">
                <div className="clients-profile-avatar" aria-hidden="true">{purchaserInitials(selected)}</div>
                <div>
                  <div className="clients-profile-name-line">
                    <h2>{purchaserName(selected)}</h2><Badge>{selected.purchaserType || 'UNVERIFIED'}</Badge>
                    <Badge variant={statusVariant(selected.status) as any}>{selected.status}</Badge>
                  </div>
                  <p>Assigned to {selected.assignedAgent?.name || 'no agent'} · Client since {formatDate(selected.createdAt)}</p>
                </div>
              </div>
            </div>
            <div className="clients-profile-details">
              <div className="clients-profile-section">
                <h3>Contact</h3>
                <div className="clients-info-grid">
                  <div><span>Phone</span><strong>{selected.phone}</strong></div>
                  <div><span>Email</span><strong>{selected.email || 'Not provided'}</strong></div>
                  <div className="clients-info-address"><span>Address</span><strong>{selected.address || 'Not provided'}</strong></div>
                  <div><span>Emergency contact</span><strong>{selected.emergencyName || 'Not provided'}</strong>{selected.emergencyPhone && <small>{selected.emergencyPhone}</small>}</div>
                </div>
              </div>
              <div className="clients-profile-section">
                <h3>Profile</h3>
                <div className="clients-info-grid">
                  <div><span>Birthday</span><strong>{selected.birthday ? formatDate(selected.birthday) : 'Not provided'}</strong></div>
                  <div><span>Occupation</span><strong>{selected.occupation || 'Not provided'}</strong></div>
                  <div><span>Income</span><strong>{selected.incomeBracket || 'Not provided'}</strong></div>
                  <div><span>Source</span><strong>{selected.source.replace(/_/g, ' ')}</strong></div>
                </div>
              </div>
            </div>
          </GlassCard>

              {/* Portal Account */}
              <GlassCard>
                <SectionHeader title="Portal Account" icon="solar:login-3-bold-duotone" />
                <div style={{ marginTop: '8px' }}>
                  {!portalStatus || !portalStatus.hasAccount ? (
                    <>
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '10px' }}>
                        {selected.email ? 'No portal account yet.' : 'Add an email to enable portal access.'}
                      </div>
                      {selected.email && (
                        <Button variant="primary" size="sm" icon="solar:letter-bold-duotone"
                          loading={inviteLoading} onClick={() => handleSendInvite(selected.id)}>
                          Send Invite
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Badge variant={
                        portalStatus.status === 'active' ? 'available' as any :
                        portalStatus.status === 'invited' ? 'reserved' as any : 'sold' as any
                      }>
                        {portalStatus.status === 'active' ? 'Active' :
                         portalStatus.status === 'invited' ? 'Awaiting activation' : 'Deactivated'}
                      </Badge>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                        {portalStatus.email}
                      </div>
                      {portalStatus.status === 'invited' && <p role="status" style={{ fontSize: '13px', lineHeight: 1.6, margin: '10px 0', color: 'var(--text-secondary)' }}>
                        {portalStatus.delivery?.status === 'SENT' ? `Email accepted by Postal${portalStatus.delivery.sentAt ? ' on ' + formatDate(portalStatus.delivery.sentAt) : ''}. Inbox delivery is not yet confirmed.` :
                         portalStatus.delivery?.status === 'SENDING' ? 'Delivery is in progress or uncertain. An administrator must check Postal before resending.' :
                         portalStatus.delivery?.status === 'FAILED' ? 'Postal rejected the invitation. Contact your administrator or retry after the configuration is corrected.' :
                         portalStatus.delivery?.status === 'EXPIRED' ? 'The previous invitation expired or was superseded. Send a new invitation.' : 'Invitation email has not been sent.'}
                      </p>}
                      {portalStatus.recoveryDelivery && <p style={{ fontSize: '13px', lineHeight: 1.6, margin: '10px 0', color: 'var(--text-secondary)' }}>
                        Last password recovery: {portalStatus.recoveryDelivery.status === 'SENT' ? 'accepted by Postal' : portalStatus.recoveryDelivery.status === 'SENDING' ? 'outcome uncertain; check Postal before retrying' : portalStatus.recoveryDelivery.status.toLowerCase()}.
                      </p>}
                      {portalStatus.lastLoginAt && (
                        <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>
                          Last login: {formatDate(portalStatus.lastLoginAt)}
                        </div>
                      )}
                      {portalStatus.status === 'invited' && (
                        <Button variant="secondary" size="sm" icon="solar:refresh-bold-duotone"
                          loading={inviteLoading} disabled={portalStatus.delivery?.status === 'SENDING'} onClick={() => handleSendInvite(selected.id)}
                          style={{ marginTop: '8px' }}>
                          Resend Invite
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </GlassCard>

          {includedSales.length > 0 && <div className="clients-financial-grid">
            <StatCard label="Sales Value" value={fmt(totalSalesAmount)}
              icon="solar:sale-bold-duotone" iconColor="#0D9488" iconBg="rgba(13,148,136,0.12)" />
            <StatCard label="Total Paid" value={fmt(totalPaid)}
              icon="solar:wallet-money-bold-duotone" iconColor="#10B981" iconBg="rgba(16,185,129,0.12)" />
            <StatCard label="Balance Due" value={fmt(totalBalance)}
              icon="solar:danger-triangle-bold-duotone" iconColor={totalBalance > 0 ? '#EF4444' : '#10B981'}
              iconBg={totalBalance > 0 ? 'rgba(239,68,68,0.12)' : 'rgba(16,185,129,0.12)'} />
            <StatCard label="Active Sales" value={String(selected.sales.filter(s => !['COMPLETED', 'CANCELLED'].includes(s.stage)).length)}
              icon="solar:document-bold-duotone" iconColor="#06B6D4" iconBg="rgba(6,182,212,0.12)" />
          </div>}

          <div className={`clients-detail-content${selected.sales.length === 0 ? ' clients-detail-content--no-sales' : ''}`}>
            <div className="clients-detail-main">

          {/* PRIORITY 3: Sales History */}
          <GlassCard hoverable={false}>
            <SectionHeader title={`Sales (${selected.sales.length})`} icon="solar:sale-bold-duotone" />
            {selected.sales.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                {selected.sales.map((s) => (
                  <div key={s.id} className="clients-sales-row" style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '14px 16px',
                    border: '1px solid var(--border-base)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <div style={{ 
                          background: 'var(--primary-600)', color: '#fff', 
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                        }}>
                          {s.lot.lotNumber}
                        </div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>
                          {s.lot.section?.label || 'Section unavailable'}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{formatDate(s.createdAt)}</div>
                    </div>
                    <div className="clients-sales-amount" style={{ textAlign: 'right' }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>{fmt(s.saleAmount)}</div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end', fontSize: '11px' }}>
                        <span style={{ color: '#10B981' }}>Paid: {fmt(s.totalPaid || 0)}</span>
                        {s.balance > 0 && <span style={{ color: '#EF4444' }}>· Bal: {fmt(s.balance)}</span>}
                      </div>
                      <Badge variant={stageVariant(s.stage) as any} style={{ marginTop: '4px' }}>{s.stage.replace(/_/g, ' ')}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="clients-detail-empty"><strong>No sales linked</strong><p>Sales for this client will appear here when a transaction is created.</p></div>
            )}
          </GlassCard>

          {/* PRIORITY 4: Payment History */}
          {ledgerError && <div className="clients-inline-error" role="alert">
            Payment history could not be loaded. <button type="button" onClick={() => openDetail(selected)}>Try again</button>
          </div>}
          {selected.ledgerEntries && selected.ledgerEntries.length > 0 && (
            <GlassCard hoverable={false}>
              <SectionHeader title={`Payment History (${selected.ledgerEntries.length})`} icon="solar:wallet-money-bold-duotone" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                {selected.ledgerEntries.slice(0, 10).map((entry) => {
                  const cfg = ledgerTypeConfig[entry.type] || { label: entry.type, icon: 'solar:document-bold-duotone', color: 'var(--text-secondary)' };
                  return (
                    <div key={entry.id} style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-elevated)', border: '1px solid var(--border-base)',
                    }}>
                      <Icon icon={cfg.icon} width={18} style={{ color: cfg.color }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{entry.description}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '11px' }}>{formatDate(entry.createdAt)}</div>
                      </div>
                      <div style={{ color: entry.amount >= 0 ? '#10B981' : '#EF4444', fontWeight: 700, fontSize: '14px' }}>
                        {entry.amount >= 0 ? '+' : ''}{fmt(entry.amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selected.ledgerEntries.length > 10 && <p className="clients-limited-note">Showing the latest 10 of {selected.ledgerEntries.length} loaded entries.</p>}
            </GlassCard>
          )}

            </div>
            <div className="clients-detail-side">

          {/* PRIORITY 5: Contact History */}
          <GlassCard hoverable={false}>
            <SectionHeader
              title={`Contact History (${selected.contactLogs.length})`}
              icon="solar:chat-round-bold-duotone"
              actions={selected.contactLogs.length > 0 ?
                <Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone"
                  onClick={() => { setLogForm({ type: 'CALL', subject: '', body: '' }); setShowLogModal(true); }}>
                  Add Log
                </Button>
              : undefined}
            />
            {selected.contactLogs.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                {(showAllLogs ? selected.contactLogs : selected.contactLogs.slice(0, 5)).map((log) => (
                  <div key={log.id} style={{
                    display: 'flex', gap: '12px', alignItems: 'flex-start',
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: '12px 14px',
                    border: '1px solid var(--border-base)',
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: 'var(--radius-sm)', flexShrink: 0,
                      background: 'rgba(13,148,136,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon icon={contactIcon(log.type)} width={16} style={{ color: 'var(--primary-400)' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="clients-log-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{log.subject}</span>
                        <span style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{formatDate(log.createdAt)}</span>
                      </div>
                      {log.body && <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.5 }}>{log.body}</div>}
                      <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                        {log.type} · {log.agent.name}
                      </div>
                    </div>
                  </div>
                ))}
                {selected.contactLogs.length > 5 && <Button variant="ghost" size="sm" onClick={() => setShowAllLogs(value => !value)}>
                  {showAllLogs ? 'Show fewer' : `Show all ${selected.contactLogs.length} loaded logs`}
                </Button>}
              </div>
            ) : (
              <div className="clients-detail-empty"><strong>No contact history</strong><p>Record the first call, message, or meeting to keep follow-ups in one place.</p><Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone" onClick={() => { setLogForm({ type: 'CALL', subject: '', body: '' }); setShowLogModal(true); }}>Add first log</Button></div>
            )}
          </GlassCard>

          {/* PRIORITY 6: Beneficiaries */}
          <GlassCard hoverable={false}>
            <SectionHeader
              title={`Beneficiaries (${selected.beneficiaries.length})`}
              icon="solar:users-group-rounded-bold-duotone"
              actions={selected.beneficiaries.length > 0 ?
                <Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone"
                  onClick={() => { setBeneficiaryForm({ firstName: '', lastName: '', relationship: '', phone: '', email: '' }); setShowBeneficiaryModal(true); }}>
                  Add
                </Button>
              : undefined}
            />
            {selected.beneficiaries.length > 0 ? (
              <div className="clients-beneficiary-grid">
                {selected.beneficiaries.map((b) => (
                  <div key={b.id} style={{
                    background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)',
                    padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    border: '1px solid var(--border-base)',
                  }}>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>{b.firstName} {b.lastName}{b.designation && <small> · {b.designation.toLowerCase()}</small>}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{b.relationship} {b.phone ? `· ${b.phone}` : ''}</div>
                    </div>
                    <Button variant="icon" aria-label={`Remove ${b.firstName} ${b.lastName}`} onClick={() => { setRemovingBeneficiary(b); setRemovalContext({ correctionReason: '', evidenceReference: '' }); }} style={{ width: '28px', height: '28px' }}>
                      <Icon icon="solar:close-circle-bold-duotone" width={16} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="clients-detail-empty"><strong>No beneficiaries</strong><p>Add a beneficiary to keep their details with this client record.</p><Button variant="secondary" size="sm" icon="solar:add-circle-bold-duotone" onClick={() => { setBeneficiaryForm({ firstName: '', lastName: '', relationship: '', phone: '', email: '' }); setShowBeneficiaryModal(true); }}>Add beneficiary</Button></div>
            )}
          </GlassCard>
            </div>
          </div>

          {/* PRIORITY 7: Notes */}
          {selected.notes && (
            <GlassCard>
              <SectionHeader title="Notes" icon="solar:document-text-bold-duotone" />
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', lineHeight: 1.6, marginTop: '8px' }}>{selected.notes}</p>
            </GlassCard>
          )}
        </div>
      </>
    );
  };

  // ─── RENDER: Form View ──────────────────────────────────

  const renderFormView = () => (
    <>
      <TopBar title={editingId ? 'Edit Client' : 'New Client'} showSearch={false} />
      <div className="clients-form-page">
        <GlassCard>
          <div className="clients-form-actions">
            <Button variant="ghost" icon="solar:arrow-left-bold" onClick={() => setView(selected ? 'detail' : 'list')}>
              Cancel
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleSaveClient} loading={saving}>
              {editingId ? 'Update' : 'Create'} Client
            </Button>
          </div>

          {editingId && <section className="client-correction-context" aria-label="Correction details">
            <h3>Correction details</h3><p>Record why this profile is changing and the source you checked, such as a document reference or dated client request. An evidence reference does not upload or approve a document.</p>
            <div className="clients-form-grid">
              <InputField label="Correction reason" required maxLength={1000} value={formData.correctionReason || ''} onChange={e => setFormData({ ...formData, correctionReason: e.target.value })} />
              <InputField label="Evidence reference" required maxLength={500} value={formData.evidenceReference || ''} onChange={e => setFormData({ ...formData, evidenceReference: e.target.value })} />
              {editingId && <CorrectionEvidence key={`profile-${editingId}`} kind="clients" ownerId={editingId} value={formData.evidenceVersionId || undefined} onChange={evidenceVersionId => setFormData({ ...formData, evidenceVersionId })} />}
            </div>
          </section>}
          <SectionHeader title="Basic Information" icon="solar:user-bold-duotone" />
          <div className="clients-form-grid" style={{ marginBottom: 20 }}>
            <label htmlFor="purchaser-type">Purchaser type<select id="purchaser-type" value={formData.purchaserType || 'UNVERIFIED'} onChange={e => setFormData({ ...formData, purchaserType: e.target.value })}>
              <option value="UNVERIFIED">Unverified legacy record</option><option value="INDIVIDUAL">Individual</option><option value="CORPORATE">Corporate</option>
            </select></label>
            {formData.purchaserType === 'CORPORATE' && <InputField label="Company name" required value={formData.companyName || ''} onChange={e => setFormData({ ...formData, companyName: e.target.value })} />}
          </div>
          <div className="clients-form-grid">
            {formData.purchaserType !== 'CORPORATE' && <>
            <InputField label="First Name" required value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
            <InputField label="Last Name" required value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
            <InputField label="Middle name" value={formData.middleName || ''} onChange={e => setFormData({ ...formData, middleName: e.target.value })} />
            </>}
            <InputField label="Phone" type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            <InputField label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            <InputField label="Address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            {formData.purchaserType !== 'CORPORATE' && <>
            <InputField label="Birthday" type="date" value={formData.birthday} onChange={(e) => setFormData({ ...formData, birthday: e.target.value })} />
            <InputField label="Occupation" value={formData.occupation} onChange={(e) => setFormData({ ...formData, occupation: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="client-income-bracket" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Income Bracket</label>
              <select id="client-income-bracket" value={formData.incomeBracket} onChange={(e) => setFormData({ ...formData, incomeBracket: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                <option value="">Select...</option>
                {INCOME_BRACKETS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            </>}
          </div>

          <div style={{ margin: '24px 0' }}><Divider /></div>

          <PurchaserFields value={formData} onChange={setFormData} />
          <SectionHeader title="CRM Details" icon="solar:info-circle-bold-duotone" />
          <div className="clients-form-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="client-source" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Source</label>
              <select id="client-source" value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                {CLIENT_SOURCES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="client-status" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Status</label>
              <select id="client-status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                {CLIENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div style={{ margin: '24px 0' }}><Divider /></div>

          <SectionHeader title="Emergency Contact" icon="solar:phone-bold-duotone" />
          <div className="clients-form-grid">
            <InputField label="Emergency Contact Name" value={formData.emergencyName} onChange={(e) => setFormData({ ...formData, emergencyName: e.target.value })} />
            <InputField label="Emergency Contact Phone" value={formData.emergencyPhone} onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })} />
          </div>

          <div style={{ margin: '24px 0' }}><Divider /></div>

          <SectionHeader title="Notes" icon="solar:document-text-bold-duotone" />
          <textarea aria-label="Client notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes about this client..."
            style={{
              width: '100%', minHeight: '100px', marginTop: '12px',
              background: 'var(--input-bg)', border: '1px solid var(--input-border)',
              borderRadius: 'var(--radius-md)', padding: '12px 14px', color: 'var(--input-text)',
              fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none', resize: 'vertical',
            }}
          />
        </GlassCard>
      </div>
    </>
  );

  // ─── Main Render ────────────────────────────────────────

  return (
    <>
      {view === 'list' && renderListView()}
      {view === 'detail' && renderDetailView()}
      {view === 'form' && renderFormView()}

      {view === 'detail' && selected && <div className="clients-detail-page"><GlassCard>
        {(!selected.purchaserType || selected.purchaserType === 'UNVERIFIED') && <p role="status">Purchaser type needs review. Check the source record, then edit the profile and record the correction evidence. Selecting a type does not certify ownership or approve documents.</p>}
        <PurchaserFields value={selected} readOnly />
        <EvidenceFiles key={`evidence-${selected.id}`} kind="clients" ownerId={selected.id} />
        <ClientCorrectionHistory key={selected.id} clientId={selected.id} updatedAt={selected.updatedAt} />
      </GlassCard></div>}
      {/* Beneficiary Modal */}
      {showBeneficiaryModal && (
        <Modal isOpen={showBeneficiaryModal} title="Add Beneficiary" onClose={() => { if (!beneficiaryOperation.current) setShowBeneficiaryModal(false); }}>
          <fieldset disabled={beneficiarySaving} style={{ border: 0, margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            <label htmlFor="beneficiary-designation">Designation<select id="beneficiary-designation" value={beneficiaryForm.designation || ''} onChange={e => setBeneficiaryForm({ ...beneficiaryForm, designation: e.target.value })}><option value="">Not recorded</option><option value="PRIMARY">Primary</option><option value="SECONDARY">Secondary</option></select></label>
            <InputField label="First Name" value={beneficiaryForm.firstName || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, firstName: e.target.value })} />
            <InputField label="Last Name" value={beneficiaryForm.lastName || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, lastName: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="beneficiary-relationship" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Relationship</label>
              <select id="beneficiary-relationship" value={beneficiaryForm.relationship || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, relationship: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                <option value="">Select...</option>
                {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <InputField label="Phone" value={beneficiaryForm.phone || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, phone: e.target.value })} />
            <InputField label="Email" value={beneficiaryForm.email || ''} onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, email: e.target.value })} />
            <InputField label="Birthday" type="date" value={beneficiaryForm.birthday || ''} onChange={e => setBeneficiaryForm({ ...beneficiaryForm, birthday: e.target.value })} />
            <p>Record the source checked for this addition. Evidence references do not upload or approve documents.</p>
            <InputField label="Addition reason" required maxLength={1000} value={beneficiaryForm.correctionReason || ''} onChange={e => setBeneficiaryForm({ ...beneficiaryForm, correctionReason: e.target.value })} />
            <InputField label="Evidence reference" required maxLength={500} value={beneficiaryForm.evidenceReference || ''} onChange={e => setBeneficiaryForm({ ...beneficiaryForm, evidenceReference: e.target.value })} />
              {selected?.id && <CorrectionEvidence key={`beneficiary-${selected?.id}`} kind="clients" ownerId={selected?.id} value={beneficiaryForm.evidenceVersionId || undefined} onChange={evidenceVersionId => setBeneficiaryForm({ ...beneficiaryForm, evidenceVersionId })} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <Button variant="ghost" disabled={beneficiarySaving} onClick={() => setShowBeneficiaryModal(false)}>Cancel</Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleAddBeneficiary} loading={beneficiarySaving}>
                Add Beneficiary
              </Button>
            </div>
          </fieldset>
        </Modal>
      )}

      {removingBeneficiary && <Modal isOpen title="Remove beneficiary" onClose={() => { if (!beneficiaryOperation.current) setRemovingBeneficiary(null); }}>
        <fieldset disabled={beneficiarySaving} className="client-beneficiary-removal">
          <p>Remove <strong>{removingBeneficiary.firstName} {removingBeneficiary.lastName}</strong> from the current beneficiary list? Their previous details will remain in correction history and saved OTP drafts.</p>
          <InputField label="Removal reason" required maxLength={1000} value={removalContext.correctionReason} onChange={e => setRemovalContext({ ...removalContext, correctionReason: e.target.value })} />
          <InputField label="Evidence reference" required maxLength={500} value={removalContext.evidenceReference} onChange={e => setRemovalContext({ ...removalContext, evidenceReference: e.target.value })} />
              {selected?.id && <CorrectionEvidence key={`removal-${selected?.id}`} kind="clients" ownerId={selected?.id} value={removalContext.evidenceVersionId || undefined} onChange={evidenceVersionId => setRemovalContext({ ...removalContext, evidenceVersionId })} />}
          <p>An evidence reference records the source checked; it does not upload or approve a document.</p>
          <div className="clients-form-actions"><Button variant="ghost" disabled={beneficiarySaving} onClick={() => setRemovingBeneficiary(null)}>Keep beneficiary</Button><Button loading={beneficiarySaving} onClick={handleRemoveBeneficiary}>Remove beneficiary</Button></div>
        </fieldset>
      </Modal>}

      {/* Contact Log Modal */}
      {showLogModal && (
        <Modal isOpen={showLogModal} title="Add Contact Log" onClose={() => setShowLogModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="contact-log-type" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Type</label>
              <select id="contact-log-type" value={logForm.type} onChange={(e) => setLogForm({ ...logForm, type: e.target.value })}
                style={{ background: 'var(--input-bg)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none' }}>
                {CONTACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <InputField label="Subject" value={logForm.subject} onChange={(e) => setLogForm({ ...logForm, subject: e.target.value })} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="contact-log-notes" style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Notes</label>
              <textarea id="contact-log-notes"
                value={logForm.body}
                onChange={(e) => setLogForm({ ...logForm, body: e.target.value })}
                placeholder="Optional details..."
                style={{
                  width: '100%', minHeight: '80px',
                  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
                  borderRadius: 'var(--radius-md)', padding: '10px 14px', color: 'var(--input-text)',
                  fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none', resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
              <Button variant="ghost" onClick={() => setShowLogModal(false)}>Cancel</Button>
              <Button variant="primary" icon="solar:check-circle-bold-duotone" onClick={handleAddLog}>
                Add Log
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Clients;
